import { AnyAction, Middleware } from 'redux';
import { action, createAction, MetaAction, PayloadAC, PayloadAction, PayloadMetaAction, TypeConstant, TypeMeta } from 'typesafe-actions';
import { StoreState } from '../types';
import { Channel } from 'async-saga';
import { SagaContext, SagaHandler } from './async-saga-types';

type Func = (...args: any) => any

interface Meta {
  api: true
}

interface ResponseAction extends AnyAction {
  meta?: {
    apiResponse?: true
  }
}

type EmptyRequestAction<TType extends TypeConstant> = MetaAction<TType, Meta>
type PayloadRequestAction<TType extends TypeConstant, TPayload> = PayloadMetaAction<TType, TPayload, Meta>

type EmptyRequestAC<TType extends TypeConstant> = () => EmptyRequestAction<TType>
type PayloadRequestAC<TType extends TypeConstant, TPayload> = (payload: TPayload) => PayloadRequestAction<TType, TPayload>

type PayloadType<Api extends Func>
  = Api extends () => any
  ? void
  : Api extends (arg: infer P) => any
  ? P
  : never

// https://github.com/microsoft/TypeScript/issues/32645#issuecomment-517088958
type RequestAC<T extends TypeConstant, P> = [P] extends [void] ? EmptyRequestAC<T> : PayloadRequestAC<T, P>
type RequestAction<T extends TypeConstant, P> = ReturnType<RequestAC<T, P>>

type ResponseAC<T extends TypeConstant, Api extends Func> = PayloadAC<T, ApiReturnType<Api>>
type ResolvePromise<T> = T extends Promise<infer R> ? R : T
type ApiReturnType<Api extends Func> = ResolvePromise<ReturnType<Api>>

interface ApiAction<TReq extends TypeConstant, TResp extends TypeConstant, Api extends Func> {
  request: RequestAC<TReq, PayloadType<Api>>
  response: PayloadAC<TResp, ResolvePromise<ReturnType<Api>>>
  api: Api
}

export function createApiRequestAction<T extends TypeConstant, A extends any[] = [], P = void, R = (...args: A) => RequestAction<T, P>>(type: T, getPayload?: (...args: A) => P): R {
  const meta: Meta = { api: true }
  const gp = getPayload || (() => undefined as unknown as P)
  return createAction(type, action => (...args: any) => action(gp(...args as A), meta)) as unknown as R
}

export function createApiAction<TReq extends TypeConstant, TResp extends TypeConstant, Api extends Func>(req: TReq, resp: TResp, api: Api): ApiAction<TReq, TResp, Api> {
  const request = createApiRequestAction(req, (arg: PayloadType<Api>) => arg) as RequestAC<TReq, PayloadType<Api>>
  const response = createAction(resp, action => (payload: ApiReturnType<Api>) => action(payload, { apiResponse: true })) as ResponseAC<TResp, Api>
  return { request, response, api }
}

type ErrorHandler = (ex: Error, ctx: SagaContext) => void

interface ApiActions {
  middleware: Middleware
  wireUpApi<TReq extends TypeConstant, TResp extends TypeConstant, Api extends Func>(apiAction: ApiAction<TReq, TResp, Api>, predicate?: (store: StoreState, payload: PayloadType<Api>) => boolean): SagaHandler
}

export function createApiActions(onError: ErrorHandler): ApiActions {
  const channel = new Channel<PayloadAction<TypeConstant, any>>()

  const takeEveryApi = <T extends TypeConstant, P>(request: RequestAC<T, P>, handler: ApiHandler<P>): SagaHandler =>
    async (ctx) => {
      for (; ;) {
        const payload = await waitForApiCall(request)
        handle(payload) // not awaiting the handler
      }

      async function handle(payload: P) {
        try {
          await handler(ctx, payload)
        } catch (e) {
          console.error('Unhandled API Handler Error', e)
        }
      }
    }

  async function invokeApi<TReq extends TypeConstant, TResp extends TypeConstant, Api extends Func>(apiAction: ApiAction<TReq, TResp, Api>, ctx: SagaContext, payload: PayloadType<Api>) {
    ctx.dispatch(action((apiAction.request as TypeMeta<TReq>).getType!(), payload))
    try {
      const resp = await apiAction.api(payload)
      ctx.dispatch(apiAction.response(resp))
    } catch (e) {
      try {
        onError(e, ctx)
      } catch (e) {
        console.error('API Error', e)
      }
    }
  }

  async function waitForApiCall<T extends TypeConstant, P>(request: RequestAC<T, P>) {
    for (; ;) {
      const ac = await channel.get()
      if (ac.type === (request as TypeMeta<TypeConstant>).getType!()) {
        return ac.payload
      }
    }
  }

  return {
    wireUpApi(apiAction, predicate = () => true) {
      return takeEveryApi(apiAction.request, async (ctx, payload) => {
        if (predicate(ctx.getState(), payload)) {
          await invokeApi(apiAction, ctx, payload)
        }
      })
    },

    middleware: store => next => (action: PayloadMetaAction<TypeConstant, any, Meta>) => {
      if (action.meta && action.meta.api) {
        console.info('API preflight', action.type, action.payload)
        channel.put(action)
      } else {
        next(action)
      }
    },
  }
}

type ApiHandler<P> = (ctx: SagaContext, payload: P) => Promise<void>


export function isApiResponse(action: ResponseAction) {
  return (action.meta && action.meta.apiResponse) || false
}

// const func = (x: string) => 123;

// type X = typeof func extends (arg: infer A) => any ? A : '2'

// const a = createApiAction('XXX', 'YYY', () => 123);
// const b = createApiAction('XXX', 'YYY', async (x: string) => 123);
// const c = createApiAction('XXX', 'YYY', (x: string, y: string) => 123);
// const d = createApiAction('XXX', 'YYY', (x: void) => 123);

// type AC<T extends TypeConstant, P = undefined> = EmptyAC<T> | PayloadAC<T, P>

// type A<AA extends AC<any>> = AA extends EmptyAC<infer T> ?

// const a1 = createAction('XXX');
// const a2 = createAction('YYY', resolve => () => resolve({ num: 123 }));
// type z = A<typeof a1>;

// export type ActionBuilderConstructor<
//   TType extends TypeConstant,
//   TPayload extends any = undefined,
//   TMeta extends any = undefined
// > = [TMeta] extends [undefined]
//   ? [TPayload] extends [undefined]
//     ? unknown extends TPayload
//       ? PayloadAC<TType, TPayload>
//       : unknown extends TMeta
//       ? PayloadMetaAC<TType, TPayload, TMeta>
//       : EmptyAC<TType>
//     : PayloadAC<TType, TPayload>
//   : PayloadMetaAC<TType, TPayload, TMeta>;


// type Test<X>
// = [X] extends [undefined]
//   ? '1'
//   : '2'

// type XX = Test<undefined>

// const x = createAsyncAction('XXX', 'YYY', 'XXX')<string, undefined, Error>()
// x.success()
